import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import { Menu, X, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { LoginDropdown } from '@/components/LoginDropdown'
import { findRouteKeyFromPath, type RouteKey } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

interface HeaderProps {
  transparent?: boolean
}

export function Header({ transparent = false }: HeaderProps) {
  const { t, i18n } = useTranslation('common')
  const location = useLocation()
  const { lang } = useParams<{ lang: string }>()
  const currentLang = (lang || i18n.language) as SupportedLanguage
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Determine current route key from path
  const getCurrentRouteKey = (): RouteKey => {
    const path = location.pathname
    const pathWithoutLang = path.replace(`/${currentLang}`, '').replace(/^\//, '')

    if (pathWithoutLang === '' || pathWithoutLang === '/') {
      return 'home'
    }

    const routeKey = findRouteKeyFromPath(pathWithoutLang, currentLang)
    return routeKey || 'home'
  }

  const currentRouteKey = getCurrentRouteKey()

  const eshopUrl = import.meta.env.VITE_ESHOP_URL

  const navItems: { key: RouteKey | string; label: string; href?: string }[] = [
    { key: 'home', label: t('nav.home') },
    { key: 'company', label: t('nav.company') },
    { key: 'products', label: t('nav.products') },
    { key: 'eshop', label: t('nav.eshop'), href: eshopUrl },
    { key: 'sustainability', label: t('nav.sustainability') },
    { key: 'openDoors', label: t('nav.openDoors') },
    { key: 'contacts', label: t('nav.contacts') },
  ]

  const isTransparent = transparent && !isScrolled

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-sticky transition-all duration-normal',
        isTransparent
          ? 'bg-transparent'
          : 'bg-white/95 backdrop-blur-sm shadow-sm'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <LocalizedLink
            to="home"
            className="flex items-center transition-opacity hover:opacity-80"
          >
            <img
              src="/images/logo-bonifanti.png"
              alt="Bonifanti"
              className="h-12 w-auto object-contain"
            />
          </LocalizedLink>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1 xl:space-x-3">
            {navItems.map((item) => {
              if (item.href) {
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'relative font-heading text-sm font-medium transition-all duration-200 rounded-lg px-2.5 xl:px-3.5 py-2 inline-flex items-center gap-1 whitespace-nowrap',
                      isTransparent
                        ? 'text-white/90 hover:text-white hover:bg-white/10'
                        : 'text-neutral-700 hover:text-brand hover:bg-neutral-900/5'
                    )}
                  >
                    {item.label}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )
              }
              const isActive = currentRouteKey === item.key
              return (
                <LocalizedLink
                  key={item.key}
                  to={item.key as RouteKey}
                  className={cn(
                    'relative font-heading text-sm font-medium transition-all duration-200 rounded-lg px-2.5 xl:px-3.5 py-2 whitespace-nowrap',
                    isTransparent
                      ? 'text-white/90 hover:text-white hover:bg-white/10'
                      : 'text-neutral-700 hover:text-brand hover:bg-neutral-900/5',
                    isActive && (isTransparent ? 'text-white bg-white/10' : 'text-brand bg-brand/5')
                  )}
                >
                  {item.label}
                  {/* Animated underline */}
                  <span
                    className={cn(
                      'absolute bottom-1 left-2.5 right-2.5 xl:left-3.5 xl:right-3.5 h-0.5 bg-current transition-all duration-300 ease-out',
                      isActive ? 'w-auto opacity-100' : 'w-0 opacity-0',
                      isActive && !isTransparent && 'bg-brand'
                    )}
                  />
                </LocalizedLink>
              )
            })}
          </nav>

          {/* Right Side: Language Selector + Mobile Menu */}
          <div className="flex items-center space-x-4">
            <div className="hidden sm:block">
              <LanguageSelector isScrolled={!isTransparent} />
            </div>

            <div className="hidden sm:block relative">
              <LoginDropdown isTransparent={isTransparent} />
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={cn(
                'lg:hidden p-2 rounded-md transition-colors',
                isTransparent
                  ? 'text-white hover:bg-white/10'
                  : 'text-neutral-700 hover:bg-neutral-100'
              )}
              aria-label={isMobileMenuOpen ? t('aria.closeMenu') : t('aria.openMenu')}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-neutral-200 shadow-lg">
          <nav className="px-4 py-4 space-y-2" aria-label={t('header.navAria')}>
            {navItems.map((item) => {
              if (item.href) {
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-1 px-3 py-2 rounded-md text-base font-heading font-medium transition-colors text-neutral-700 hover:text-brand hover:bg-neutral-50"
                  >
                    {item.label}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )
              }
              const isActive = currentRouteKey === item.key
              return (
                <LocalizedLink
                  key={item.key}
                  to={item.key as RouteKey}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    'block px-3 py-2 rounded-md text-base font-heading font-medium transition-colors',
                    isActive
                      ? 'text-brand bg-brand/5'
                      : 'text-neutral-700 hover:text-brand hover:bg-neutral-50'
                  )}
                >
                  {item.label}
                </LocalizedLink>
              )
            })}
            <div className="pt-4 border-t border-neutral-200">
              <LanguageSelector variant="mobile" />
            </div>
            <LoginDropdown isTransparent={false} variant="mobile" />
          </nav>
        </div>
      )}
    </header>
  )
}
